import { ProductComponent } from '@theme/view-event-elements';
import { getSpeculativeLoadingBudget, promoteLazyImageForCurrentCatalogView } from '@theme/utilities';
import { MediaStartedPlayingEvent } from '@theme/events';

const CARD_RAIL_SCROLL_END_FALLBACK_MS = 90;
const CARD_MEDIA_PROGRESS_IDLE_MS = 1400;
const CARD_LOOKAHEAD_PX = {
  none: 250,
  conservative: 500,
  balanced: 900,
};
const CARD_MEDIA_REVEAL_FAILSAFE_MS = 5000;
let cardLookaheadObserver;
let cardEnhancementObserver;
let cardRevealObserver;
let cardAdjacentMediaObserver;
const adjacentMediaWarmQueue = new Set();
let adjacentMediaWarmJobs = 0;

function flushAdjacentMediaWarmQueue() {
  while (adjacentMediaWarmJobs < 2 && adjacentMediaWarmQueue.size) {
    const card = adjacentMediaWarmQueue.values().next().value;
    adjacentMediaWarmQueue.delete(card);
    if (!card.isConnected || getSpeculativeLoadingBudget() !== 'balanced') continue;
    const bounds = card.getBoundingClientRect();
    if (bounds.bottom <= 0 || bounds.top > window.innerHeight + 200) {
      cardAdjacentMediaObserver?.observe(card);
      continue;
    }
    adjacentMediaWarmJobs += 1;
    void card.warmNextMedia().catch(() => false).finally(() => {
      adjacentMediaWarmJobs -= 1;
      flushAdjacentMediaWarmQueue();
    });
  }
}

function getCardAdjacentMediaObserver() {
  if (cardAdjacentMediaObserver) return cardAdjacentMediaObserver;
  if (!('IntersectionObserver' in window)) return null;
  cardAdjacentMediaObserver = new IntersectionObserver((entries, observer) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || getSpeculativeLoadingBudget() !== 'balanced') continue;
      const card = entry.target;
      if (!(card instanceof ProductCard)) continue;
      adjacentMediaWarmQueue.add(card);
      observer.unobserve(card);
    }
    flushAdjacentMediaWarmQueue();
  }, { rootMargin: '0px 0px 200px 0px' });
  return cardAdjacentMediaObserver;
}
/** @type {Set<ProductCard>} */
const readyMediaRevealCards = new Set();
/** @type {number | undefined} */
let readyMediaRevealFrame;
const collectionRevealOwners = new WeakMap();

/**
 * Flush decoded scroll-surface media together, including cards ahead of the viewport.
 * @param {ProductCard} card
 */
function queueReadyMediaReveal(card) {
  const collection = card.closest('collection-component > results-list[data-catalog-view-contract="standard-lookbook"]');
  if (collection?.queueCardMediaReveal?.(card)) {
    collectionRevealOwners.set(card, collection);
    return;
  }
  const main = card.closest('main#MainContent');
  const homeOrPdp = main && /^(index|product)(\.|$)/.test(main.dataset.template || '') && !card.closest('dialog, [role="dialog"]');
  // Other product-card consumers keep their existing immediate reveal path.
  if (!collection && !homeOrPdp) {
    const bounds = card.getBoundingClientRect();
    if (bounds.top < window.innerHeight && bounds.bottom > 0) card.revealReadyMedia();
    return;
  }
  readyMediaRevealCards.add(card);
  if (readyMediaRevealFrame !== undefined) return;
  readyMediaRevealFrame = window.requestAnimationFrame(() => {
    readyMediaRevealFrame = undefined;
    const cards = Array.from(readyMediaRevealCards);
    readyMediaRevealCards.clear();
    for (const candidate of cards) {
      if (candidate.isConnected) candidate.revealReadyMedia();
    }
  });
}

/** @param {ProductCard} card */
function cancelReadyMediaReveal(card) {
  collectionRevealOwners.get(card)?.cancelCardMediaReveal?.(card);
  collectionRevealOwners.delete(card);
  readyMediaRevealCards.delete(card);
  if (readyMediaRevealCards.size || readyMediaRevealFrame === undefined) return;
  window.cancelAnimationFrame(readyMediaRevealFrame);
  readyMediaRevealFrame = undefined;
}

function getCardRevealObserver() {
  if (cardRevealObserver) return cardRevealObserver;
  if (!('IntersectionObserver' in window)) return null;

  cardRevealObserver = new IntersectionObserver((entries, observer) => {
    for (const entry of entries) {
      if (!entry.isIntersecting || !(entry.target instanceof ProductCard)) continue;
      if (!entry.target.isMediaRevealReady()) continue;
      queueReadyMediaReveal(entry.target);
    }
  });

  return cardRevealObserver;
}

function getCardEnhancementObserver() {
  if (cardEnhancementObserver) return cardEnhancementObserver;
  if (!('IntersectionObserver' in window)) return null;

  const budget = getSpeculativeLoadingBudget();
  const rootMargin = budget === 'balanced' ? 600 : budget === 'conservative' ? 300 : 0;

  cardEnhancementObserver = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const card = entry.target;
        if (card instanceof ProductCard) card.activateEnhancements();
        observer.unobserve(card);
      }
    },
    { rootMargin: `0px 0px ${rootMargin}px 0px` }
  );

  return cardEnhancementObserver;
}

function getCardLookaheadObserver() {
  if (cardLookaheadObserver) return cardLookaheadObserver;

  const budget = getSpeculativeLoadingBudget();
  const rootMargin = CARD_LOOKAHEAD_PX[budget] ?? 0;

  cardLookaheadObserver = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const card = entry.target;
        if (card instanceof ProductCard) card.warmLikelyMedia();
        observer.unobserve(card);
      }
    },
    { rootMargin: `0px 0px ${rootMargin}px 0px` }
  );

  return cardLookaheadObserver;
}

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    if (event.defaultPrevented) return;

    if (event.target instanceof Element) {
      if (event.target.closest('[data-tfp-brand-link]')) return;
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {HTMLElement} [slideshow] - Inert browser-native media rail markup.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  static #activeVideoCard;

  #railActive = false;
  #railScrollFrame;
  #mediaRevealTimeout;
  #mediaRevealReady = false;
  #mediaRevealGeneration = 0;
  #mediaRevealImage;
  #mediaRevealAbortController;
  #railScrollEndTimer;
  #mediaProgressHideTimer;
  #deferredHydrationPromise;
  #deferredHydrationAbortController;
  #deferredHydrationGeneration = 0;
  #deferredHydrated = false;
  #activeMediaIndex = 0;
  #collectionView = 'standard';
  #pendingCollectionMediaIndex;
  #standardMediaSlideId;
  #standardVideoManuallyPaused = false;
  #lookbookPreferredSlide;
  #lookbookPreferredPreviousSlide;
  #lookbookPreferredNextSlide;
  #cardVideoVisible = false;
  #videoManuallyPaused = false;
  #pendingManualVideoPlay = false;
  #videoObserver;
  #videoAbortController;
  #enhancementsActive = false;
  #galleryIntentConfirmed = false;
  #galleryPointerId;
  #galleryPointerStartX;
  #galleryPointerStartY;
  #pointerIntentListenersDeferred = false;
  #adjacentMediaWarmImage;
  #adjacentMediaWarmPromise;
  #adjacentMediaWarmController;
  #adjacentMediaWaitingForPrimary = false;

  get productPageUrl() {
    return this.refs.productCardLink?.href || '';
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  syncCollectionView(view) {
    if (
      (view !== 'standard' && view !== 'lookbook' && view !== 'single') ||
      !this.closest("results-list[data-catalog-view-contract='standard-lookbook']") ||
      !this.#nativeRail()
    ) {
      return;
    }

    this.#syncCollectionImageSizes(view);
    if (view === this.#collectionView) return;

    const slides = this.#mediaSlides();
    if (!slides.length) {
      this.#collectionView = view;
      return;
    }

    if (view === 'lookbook') {
      const selectedSlide = slides[this.#activeMediaIndex] || slides[0];
      this.#standardMediaSlideId = selectedSlide?.getAttribute('slide-id') || undefined;
      this.#standardVideoManuallyPaused = this.#videoManuallyPaused;
      this.#collectionView = view;
      this.#syncMediaProgress();

      const preferredSlide = slides.find((slide) => slide.hasAttribute('data-lookbook-preferred'));
      if (preferredSlide) {
        const scroller = this.#railScroller();
        if (scroller && preferredSlide.parentElement === scroller) {
          this.#lookbookPreferredSlide = preferredSlide;
          this.#lookbookPreferredPreviousSlide = preferredSlide.previousElementSibling;
          this.#lookbookPreferredNextSlide = preferredSlide.nextElementSibling;
          if (slides.indexOf(preferredSlide) > 0) scroller.prepend(preferredSlide);
        }

        if (preferredSlide.hasAttribute('data-product-card-video')) {
          this.#videoManuallyPaused = true;
          this.#pendingManualVideoPlay = false;
          this.#pauseCardVideo();
        }
        const preferredIndex = this.#mediaSlides().indexOf(preferredSlide);
        if (preferredIndex >= 0) this.#syncCollectionMediaScroll(preferredIndex);
      }
      return;
    }

    this.#collectionView = view;
    this.#syncMediaProgress();
    this.#videoManuallyPaused = this.#standardVideoManuallyPaused;
    const scroller = this.#railScroller();
    if (scroller && this.#lookbookPreferredSlide?.parentElement === scroller) {
      if (this.#lookbookPreferredPreviousSlide?.parentElement === scroller) {
        this.#lookbookPreferredPreviousSlide.after(this.#lookbookPreferredSlide);
      } else if (this.#lookbookPreferredNextSlide?.parentElement === scroller) {
        scroller.insertBefore(this.#lookbookPreferredSlide, this.#lookbookPreferredNextSlide);
      } else if (!this.#lookbookPreferredPreviousSlide) {
        scroller.prepend(this.#lookbookPreferredSlide);
      } else {
        scroller.append(this.#lookbookPreferredSlide);
      }
    }

    const standardSlide = this.#allMediaSlides().find(
      (slide) => slide.getAttribute('slide-id') === this.#standardMediaSlideId
    );
    const standardIndex = standardSlide ? this.#mediaSlides().indexOf(standardSlide) : -1;
    if (standardIndex >= 0) this.#syncCollectionMediaScroll(standardIndex);
    this.#standardMediaSlideId = undefined;
    this.#lookbookPreferredSlide = undefined;
    this.#lookbookPreferredPreviousSlide = undefined;
    this.#lookbookPreferredNextSlide = undefined;
  }

  #syncCollectionImageSizes(view) {
    const gallery = this.refs.cardGallery;
    if (!(gallery instanceof HTMLElement)) return;

    const sizesOwner = gallery.hasAttribute('data-image-sizes-standard')
      ? gallery : gallery.closest('results-list[data-image-sizes-standard]');
    const sizes = view === 'lookbook'
      ? sizesOwner?.dataset.imageSizesLookbook
      : view === 'single' ? sizesOwner?.dataset.imageSizesSingle : sizesOwner?.dataset.imageSizesStandard;
    if (!sizes) return;

    for (const image of gallery.querySelectorAll('img[srcset]')) {
      if (!(image instanceof HTMLImageElement)) continue;
      const nextSizes = image.loading === 'lazy' ? `auto, ${sizes}` : sizes;
      if (image.sizes !== nextSizes) image.sizes = nextSizes;
    }
  }

  #nativeRail() {
    const rail = this.refs.slideshow;
    return rail instanceof HTMLElement && rail.hasAttribute('data-product-card-native') ? rail : null;
  }

  #railScroller() {
    const rail = this.#nativeRail();
    const scroller = rail?.querySelector('[ref="scroller"]');
    return scroller instanceof HTMLElement ? scroller : null;
  }

  #allMediaSlides() {
    const scroller = this.#railScroller();
    if (!scroller) return [];
    return Array.from(scroller.querySelectorAll(':scope > slideshow-slide'));
  }

  #mediaSlides() {
    return this.#allMediaSlides().filter((slide) => !slide.hasAttribute('hidden'));
  }

  #cardVideoSlide() {
    return this.#mediaSlides().find((slide) => slide.hasAttribute('data-product-card-video')) || null;
  }

  #cardVideoToggle() {
    const control = this.querySelector('[data-product-card-video-toggle]');
    return control instanceof HTMLButtonElement ? control : null;
  }

  #setCardVideoControlState(isPlaying) {
    const control = this.#cardVideoToggle();
    if (!control) return;

    control.dataset.state = isPlaying ? 'playing' : 'paused';
    control.setAttribute('aria-label', isPlaying ? control.dataset.pauseLabel || '' : control.dataset.playLabel || '');
  }

  #canAutoplayCardVideo() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    return (
      this.#collectionView !== 'lookbook' &&
      !reducedMotion &&
      getSpeculativeLoadingBudget() !== 'none'
    );
  }

  #pauseCardVideo() {
    const videoSlide = this.#cardVideoSlide();
    const deferredMedia = videoSlide?.querySelector('deferred-media');
    if (deferredMedia instanceof HTMLElement) deferredMedia.pauseMedia?.();

    if (ProductCard.#activeVideoCard === this) ProductCard.#activeVideoCard = undefined;
    this.#setCardVideoControlState(false);
  }

  async #playCardVideo({ manual = false } = {}) {
    const videoSlide = this.#cardVideoSlide();
    if (
      !(videoSlide instanceof HTMLElement) ||
      videoSlide.getAttribute('aria-hidden') !== 'false' ||
      !this.#cardVideoVisible ||
      document.hidden ||
      this.#videoManuallyPaused ||
      (!manual && !this.#canAutoplayCardVideo())
    ) {
      return;
    }

    const deferredMedia = videoSlide.querySelector('deferred-media');
    if (!(deferredMedia instanceof HTMLElement)) return;

    // Section-rendered cards can introduce the first video after page load;
    // script tags inside the appended HTML do not execute.
    if (!customElements.get('deferred-media')) {
      try {
        await import('@theme/media');
      } catch (error) {
        console.warn('[product-card] Video runtime failed:', error);
        return;
      }
    }
    await customElements.whenDefined('deferred-media');
    if (
      !this.isConnected ||
      videoSlide.getAttribute('aria-hidden') !== 'false' ||
      !this.#cardVideoVisible ||
      document.hidden ||
      this.#videoManuallyPaused ||
      (!manual && !this.#canAutoplayCardVideo())
    ) {
      return;
    }

    const existingVideo = deferredMedia.querySelector('video');
    if (existingVideo instanceof HTMLVideoElement && !existingVideo.paused) {
      ProductCard.#activeVideoCard = this;
      this.#setCardVideoControlState(true);
      return;
    }

    ProductCard.#activeVideoCard?.#pauseCardVideo();

    // DeferredMedia announces its first load itself. Replays must announce too,
    // so the existing theme-wide contract pauses a visible PDP/recommendation video.
    // The shared DeferredMedia owner excludes the originating element.
    if (deferredMedia.hasAttribute('data-media-loaded')) {
      deferredMedia.dispatchEvent(new MediaStartedPlayingEvent(deferredMedia));
    }

    const templateVideo = deferredMedia.querySelector('template video');
    if (templateVideo instanceof HTMLVideoElement) templateVideo.removeAttribute('autoplay');

    deferredMedia.loadContent?.(false);
    const video = deferredMedia.querySelector('video');

    if (video instanceof HTMLVideoElement) {
      const signal = this.#videoAbortController?.signal;
      if (signal && !signal.aborted && !video.dataset.productCardVideoEvents) {
        video.dataset.productCardVideoEvents = 'true';
        video.addEventListener('play', this.#handleCardVideoPlayback, { signal });
        video.addEventListener('pause', this.#handleCardVideoPlayback, { signal });
        video.addEventListener('ended', this.#handleCardVideoPlayback, { signal });
        video.addEventListener('error', this.#handleCardVideoPlayback, { signal });
      }
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      try {
        await video.play();
      } catch {
        this.#setCardVideoControlState(false);
        return;
      }
    } else {
      deferredMedia.playMedia?.();
    }

    ProductCard.#activeVideoCard = this;
    this.#setCardVideoControlState(true);
  }

  #syncCardVideoPlayback(index) {
    const videoSlide = this.#cardVideoSlide();
    const videoIndex = videoSlide ? this.#mediaSlides().indexOf(videoSlide) : -1;

    if (videoIndex < 0 || index !== videoIndex) {
      this.#pauseCardVideo();
      return;
    }

    if (!this.#videoManuallyPaused && this.#cardVideoVisible) {
      const manual = this.#pendingManualVideoPlay;
      this.#pendingManualVideoPlay = false;
      void this.#playCardVideo({ manual });
    }
  }

  #handleCardVideoToggle = (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-product-card-video-toggle]');
    if (!(control instanceof HTMLButtonElement) || !this.contains(control)) return;

    event.preventDefault();
    event.stopPropagation();

    const videoSlide = this.#cardVideoSlide();
    if (!videoSlide) return;

    const videoIndex = this.#mediaSlides().indexOf(videoSlide);
    const isVideoSelected = videoIndex === this.#activeMediaIndex;
    const isPlaying = control.dataset.state === 'playing';

    if (!isVideoSelected) {
      this.#videoManuallyPaused = false;
      this.#pendingManualVideoPlay = true;
      this.#activateMediaRail();
      this.#scrollToMediaIndex(videoIndex);
      return;
    }

    if (isPlaying) {
      this.#videoManuallyPaused = true;
      this.#pauseCardVideo();
      return;
    }

    this.#videoManuallyPaused = false;
    this.#pendingManualVideoPlay = true;
    this.#syncCardVideoPlayback(this.#activeMediaIndex);
  };

  #handleCardVideoVisibility = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    this.#cardVideoVisible = entry.isIntersecting && entry.intersectionRatio >= 0.6;
    if (!this.#cardVideoVisible) {
      this.#pauseCardVideo();
      return;
    }

    this.#syncCardVideoPlayback(this.#activeMediaIndex);
  };

  #handleDocumentVisibility = () => {
    if (document.hidden) {
      this.#pauseCardVideo();
      return;
    }

    this.#syncCardVideoPlayback(this.#activeMediaIndex);
  };

  #handleCardVideoPlayback = (event) => {
    const videoSlide = this.#cardVideoSlide();
    if (!(videoSlide instanceof HTMLElement) || !videoSlide.contains(event.currentTarget)) return;

    if (event.type === 'play') {
      if (ProductCard.#activeVideoCard !== this) ProductCard.#activeVideoCard?.#pauseCardVideo();
      ProductCard.#activeVideoCard = this;
      this.#setCardVideoControlState(true);
    } else {
      if (ProductCard.#activeVideoCard === this) ProductCard.#activeVideoCard = undefined;
      this.#setCardVideoControlState(false);
    }
  };

  #setupCardVideoLifecycle() {
    if (!this.#cardVideoSlide()) return;

    this.#videoAbortController = new AbortController();
    const { signal } = this.#videoAbortController;
    document.addEventListener('visibilitychange', this.#handleDocumentVisibility, { signal });

    const videoSlide = this.#cardVideoSlide();
    const deferredMedia = videoSlide?.querySelector('deferred-media');
    const video = deferredMedia?.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      video.addEventListener('play', this.#handleCardVideoPlayback, { signal });
      video.addEventListener('pause', this.#handleCardVideoPlayback, { signal });
      video.addEventListener('ended', this.#handleCardVideoPlayback, { signal });
      video.addEventListener('error', this.#handleCardVideoPlayback, { signal });
    }

    if (!('IntersectionObserver' in window)) {
      this.#cardVideoVisible = true;
      return;
    }

    this.#videoObserver = new IntersectionObserver(this.#handleCardVideoVisibility, { threshold: [0, 0.6] });
    this.#videoObserver.observe(videoSlide);
  }

  #activateMediaRail() {
    if (this.#railActive) return;
    const scroller = this.#railScroller();
    if (!scroller) return;

    this.#railActive = true;
    scroller.addEventListener('scroll', this.#handleMediaRailScroll, { passive: true });
    if ('onscrollend' in scroller) {
      scroller.addEventListener('scrollend', this.#handleMediaRailScrollEnd, { passive: true });
    }
  }

  #deactivateMediaRail() {
    this.#hideMediaProgress();
    const scroller = this.#railScroller();
    if (scroller) {
      scroller.removeEventListener('scroll', this.#handleMediaRailScroll);
      scroller.removeEventListener('scrollend', this.#handleMediaRailScrollEnd);
    }
    if (this.#railScrollFrame !== undefined) window.cancelAnimationFrame(this.#railScrollFrame);
    this.#railScrollFrame = undefined;
    window.clearTimeout(this.#railScrollEndTimer);
    this.#railActive = false;
  }

  #handleMediaRailIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const rail = event.target.closest('slideshow-component[data-product-card-native]');
    if (!(rail instanceof HTMLElement) || !this.contains(rail)) return;

    this.activateEnhancements();
    this.#activateMediaRail();
    if (this.#mediaSlides().length === 1) this.#materializeLocalDeferredMedia();

    void this.warmNextMedia(true);

    if (event.type === 'focusin') {
      this.#showMediaProgress();
      this.#scheduleMediaProgressHide();
      if (rail.hasAttribute('data-deferred-media')) void this.hydrateDeferredMedia();
      return;
    }

    if (!(event instanceof PointerEvent)) return;
    if (this.#pointerIntentListenersDeferred) {
      this.#clearMediaRailPointerIntent();
      this.addEventListener('pointermove', this.#handleMediaRailPointerMove, { passive: true });
      this.addEventListener('pointerup', this.#clearMediaRailPointerIntent, { passive: true });
      this.addEventListener('pointercancel', this.#clearMediaRailPointerIntent, { passive: true });
    }
    this.#galleryPointerId = event.pointerId;
    this.#galleryPointerStartX = event.clientX;
    this.#galleryPointerStartY = event.clientY;
  };

  #handleMediaRailPointerMove = (event) => {
    if (
      !(event instanceof PointerEvent) ||
      event.pointerId !== this.#galleryPointerId ||
      this.#galleryPointerStartX === undefined ||
      this.#galleryPointerStartY === undefined
    ) {
      return;
    }

    const deltaX = Math.abs(event.clientX - this.#galleryPointerStartX);
    const deltaY = Math.abs(event.clientY - this.#galleryPointerStartY);
    const intentThreshold = 5;

    if (deltaY >= intentThreshold && deltaY > deltaX) {
      this.#clearMediaRailPointerIntent();
      return;
    }

    if (deltaX < intentThreshold || deltaX <= deltaY) return;

    const rail = event.target instanceof Element
      ? event.target.closest('slideshow-component[data-product-card-native]')
      : null;
    if (rail instanceof HTMLElement && this.contains(rail)) {
      this.#showMediaProgress();
      this.#scheduleMediaProgressHide();
    }
    if (rail instanceof HTMLElement && this.contains(rail) && rail.hasAttribute('data-deferred-media')) {
      this.#galleryIntentConfirmed = true;
      this.#materializeLocalDeferredMedia();
      void this.hydrateDeferredMedia();
    }
    this.#clearMediaRailPointerIntent();
  };

  #clearMediaRailPointerIntent = () => {
    if (this.#pointerIntentListenersDeferred) {
      this.removeEventListener('pointermove', this.#handleMediaRailPointerMove);
      this.removeEventListener('pointerup', this.#clearMediaRailPointerIntent);
      this.removeEventListener('pointercancel', this.#clearMediaRailPointerIntent);
    }
    this.#galleryPointerId = undefined;
    this.#galleryPointerStartX = undefined;
    this.#galleryPointerStartY = undefined;
  };

  #handleMediaRailScroll = () => {
    const scroller = this.#railScroller();
    if (!scroller) return;
    this.#showMediaProgress();

    if (this.#railScrollFrame === undefined) {
      this.#railScrollFrame = window.requestAnimationFrame(() => {
        this.#railScrollFrame = undefined;
        this.#syncMediaRailState();
      });
    }

    if ('onscrollend' in scroller) return;

    window.clearTimeout(this.#railScrollEndTimer);
    this.#railScrollEndTimer = window.setTimeout(this.#handleMediaRailScrollEnd, CARD_RAIL_SCROLL_END_FALLBACK_MS);
  };

  #handleMediaRailScrollEnd = () => {
    if (this.#railScrollFrame !== undefined) window.cancelAnimationFrame(this.#railScrollFrame);
    this.#railScrollFrame = undefined;

    const currentIndex = this.#currentMediaIndex();
    this.#syncMediaRailState(currentIndex);
    this.#scheduleMediaProgressHide();
    if (this.#pendingManualVideoPlay) {
      const videoSlide = this.#cardVideoSlide();
      if (!videoSlide || this.#mediaSlides().indexOf(videoSlide) !== currentIndex) {
        this.#pendingManualVideoPlay = false;
      }
    }

    const rail = this.#nativeRail();
    if (rail?.hasAttribute('data-deferred-media')) void this.hydrateDeferredMedia();

    if (currentIndex > 0) {
      this.#galleryIntentConfirmed = true;
      void this.warmNextMedia(true);
    }
  };

  #currentMediaIndex() {
    const scroller = this.#railScroller();
    const slides = this.#mediaSlides();
    if (!scroller || !slides.length) return 0;

    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCenter - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    return bestIndex;
  }

  #syncMediaRailState(index = this.#currentMediaIndex()) {
    const rail = this.#nativeRail();
    if (!rail) return;

    const slides = this.#mediaSlides();
    if (!slides.length) return;

    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
    this.#activeMediaIndex = safeIndex;
    slides.forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', slideIndex === safeIndex ? 'false' : 'true');
      for (const link of slide.querySelectorAll('[data-card-media-link]')) {
        if (!(link instanceof HTMLAnchorElement)) continue;
        link.tabIndex = slideIndex === safeIndex ? 0 : -1;
        if (this.refs.productCardLink?.href) link.href = this.refs.productCardLink.href;
      }
    });

    const previous = rail.querySelector('.slideshow-control--previous');
    const next = rail.querySelector('.slideshow-control--next');
    if (previous instanceof HTMLButtonElement) previous.disabled = safeIndex === 0;
    if (next instanceof HTMLButtonElement) {
      next.disabled = safeIndex >= slides.length - 1 && !rail.hasAttribute('data-deferred-media');
    }

    this.#syncMediaProgress(safeIndex);
    this.#syncCardVideoPlayback(safeIndex);
  }

  #syncMediaProgress(index = this.#activeMediaIndex, materialize = false) {
    const rail = this.#nativeRail();
    const progress = this.refs.cardGallery?.querySelector('[data-card-media-progress]');
    if (!(rail instanceof HTMLElement) || !(progress instanceof HTMLElement)) return;

    const railTotal = Number.parseInt(rail.dataset.cardMediaCount || '', 10);
    const fallbackTotal = this.#mediaSlides().length;
    const total = Number.isFinite(railTotal) && railTotal > 0 ? railTotal : fallbackTotal;
    const gridView = this.closest('[product-grid-view]')?.getAttribute('product-grid-view');
    if (total <= 1 || this.#collectionView === 'lookbook' ||
        gridView === 'lookbook' || gridView === 'editorial' || gridView === 'quick-buy') {
      this.#hideMediaProgress();
      progress.hidden = true;
      return;
    }

    let dots = Array.from(progress.querySelectorAll('[data-card-media-dot]'));
    if (!dots.length && !materialize) return;
    if (dots.length !== total) {
      progress.replaceChildren(
        ...Array.from({ length: total }, () => {
          const dot = document.createElement('span');
          dot.className = 'card-gallery__media-dot';
          dot.setAttribute('data-card-media-dot', '');
          return dot;
        })
      );
      dots = Array.from(progress.querySelectorAll('[data-card-media-dot]'));
    }

    const safeIndex = Math.max(0, Math.min(index, total - 1));
    dots.forEach((dot, dotIndex) => dot.toggleAttribute('data-active', dotIndex === safeIndex));
    progress.dataset.cardMediaTotal = String(total);
    progress.dataset.cardMediaCurrent = String(safeIndex + 1);
    progress.hidden = false;
  }

  #showMediaProgress() {
    const progress = this.refs.cardGallery?.querySelector('[data-card-media-progress]');
    if (!(progress instanceof HTMLElement)) return;
    this.#syncMediaProgress(this.#activeMediaIndex, true);
    if (progress.hidden) return;
    window.clearTimeout(this.#mediaProgressHideTimer);
    this.#mediaProgressHideTimer = undefined;
    progress.setAttribute('data-visible', '');
  }

  #scheduleMediaProgressHide() {
    const progress = this.refs.cardGallery?.querySelector('[data-card-media-progress]');
    if (!(progress instanceof HTMLElement) || !progress.hasAttribute('data-visible')) return;
    window.clearTimeout(this.#mediaProgressHideTimer);
    this.#mediaProgressHideTimer = window.setTimeout(() => {
      this.#mediaProgressHideTimer = undefined;
      progress.removeAttribute('data-visible');
    }, CARD_MEDIA_PROGRESS_IDLE_MS);
  }

  #hideMediaProgress() {
    window.clearTimeout(this.#mediaProgressHideTimer);
    this.#mediaProgressHideTimer = undefined;
    this.refs.cardGallery?.querySelector('[data-card-media-progress]')?.removeAttribute('data-visible');
  }

  #syncCollectionMediaScroll(index) {
    // Geometry reads inside a skipped card defeat content-visibility and lay out
    // the entire PLP. Reuse the existing near-viewport/intent activation instead.
    const observer = getCardEnhancementObserver();
    if (observer && typeof this.checkVisibility === 'function' &&
        !this.checkVisibility({ contentVisibilityAuto: true })) {
      this.#pendingCollectionMediaIndex = index;
      this.#syncMediaRailState(index);
      observer.observe(this);
      return;
    }
    this.#pendingCollectionMediaIndex = undefined;
    this.#scrollToMediaIndex(index, { animate: false });
  }

  #flushCollectionMediaScroll() {
    const index = this.#pendingCollectionMediaIndex;
    if (index === undefined) return;
    this.#pendingCollectionMediaIndex = undefined;
    this.#scrollToMediaIndex(index, { animate: false });
  }

  #scrollToMediaIndex(index, { animate = true } = {}) {
    const scroller = this.#railScroller();
    const slides = this.#mediaSlides();
    if (!scroller || !slides.length) return;

    const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
    const slide = slides[safeIndex];
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = animate && !reduceMotion ? 'smooth' : 'auto';
    const left = slide.offsetLeft - Math.max(0, (scroller.clientWidth - slide.clientWidth) / 2);

    scroller.scrollTo({ left, behavior });
    this.#syncMediaRailState(safeIndex);
    if (animate) {
      this.#showMediaProgress();
      this.#scheduleMediaProgressHide();
    }
  }

  #handleMediaRailClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('.slideshow-control--previous, .slideshow-control--next');
    const rail = button?.closest('slideshow-component[data-product-card-native]');
    if (!(button instanceof HTMLButtonElement) || !(rail instanceof HTMLElement) || !this.contains(rail)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#activateMediaRail();

    let slides = this.#mediaSlides();
    let current = this.#currentMediaIndex();
    const direction = button.classList.contains('slideshow-control--previous') ? -1 : 1;

    if (direction > 0 && current >= slides.length - 1 && rail.hasAttribute('data-deferred-media')) {
      this.#galleryIntentConfirmed = true;
      const materializedLocalMedia = this.#materializeLocalDeferredMedia();
      if (materializedLocalMedia) {
        void this.hydrateDeferredMedia();
      } else {
        await this.hydrateDeferredMedia();
      }
      slides = this.#mediaSlides();
      current = Math.min(current, slides.length - 1);
    }

    if (!slides.length) return;
    this.#scrollToMediaIndex(current + direction);
  };

  #materializeLocalDeferredMedia() {
    const rail = this.#nativeRail();
    const scroller = this.#railScroller();
    const template = rail?.querySelector('template[data-card-media-local]');
    if (!rail || !scroller || !(template instanceof HTMLTemplateElement)) return false;

    const existingIds = new Set(
      Array.from(scroller.querySelectorAll(':scope > slideshow-slide[slide-id]'), (slide) => slide.getAttribute('slide-id'))
    );

    let appended = false;
    for (const slide of Array.from(template.content.children)) {
      if (!(slide instanceof HTMLElement) || slide.tagName.toLowerCase() !== 'slideshow-slide') continue;
      const slideId = slide.getAttribute('slide-id');
      if (slideId && existingIds.has(slideId)) continue;

      scroller.appendChild(slide.cloneNode(true));
      if (slideId) existingIds.add(slideId);
      appended = true;
    }
    template.remove();

    if (!appended) return false;

    this.#syncMediaRailState();
    this.#syncCollectionImageSizes(this.#collectionView);

    const localSlide = this.#mediaSlides().at(-1);
    const localImage = localSlide?.querySelector('img');
    if (localImage instanceof HTMLImageElement) {
      promoteLazyImageForCurrentCatalogView(localImage);
      localImage.fetchPriority = 'auto';
      void localImage.decode?.().catch(() => {});
    }


    const expectedMediaCount = Number(rail.dataset.cardMediaCount);
    if (Number.isFinite(expectedMediaCount) && expectedMediaCount > 0 && this.#mediaSlides().length >= expectedMediaCount) {
      this.#deferredHydrated = true;
      rail.removeAttribute('data-deferred-media');
    }

    return true;
  }

  #appendDeferredMedia(sourceDocument) {
    const rail = this.#nativeRail();
    const scroller = this.#railScroller();
    const fragment = sourceDocument?.querySelector?.('[data-card-media-rail]');
    if (!rail || !scroller || !(fragment instanceof HTMLElement)) return false;

    rail.querySelector('template[data-card-media-local]')?.remove();

    const existingIds = new Set(
      Array.from(scroller.querySelectorAll(':scope > slideshow-slide[slide-id]'), (slide) => slide.getAttribute('slide-id'))
    );

    let appended = false;
    for (const slide of Array.from(fragment.children)) {
      if (!(slide instanceof HTMLElement) || slide.tagName.toLowerCase() !== 'slideshow-slide') continue;
      const slideId = slide.getAttribute('slide-id');
      if (slideId && existingIds.has(slideId)) continue;

      scroller.appendChild(slide.cloneNode(true));
      if (slideId) existingIds.add(slideId);
      appended = true;
    }

    this.#deferredHydrated = true;
    rail.removeAttribute('data-deferred-media');
    this.#syncMediaRailState();
    return appended || true;
  }

  #revealPrimaryMedia() {
    const gallery = this.refs.cardGallery;
    if (!(gallery instanceof HTMLElement)) return;

    const image = this.#mediaSlides()[0]?.querySelector('img') || gallery.querySelector('.product-media-container img');
    if (image === this.#mediaRevealImage && gallery.hasAttribute('data-media-pending')) return;
    const generation = ++this.#mediaRevealGeneration;
    cancelReadyMediaReveal(this);
    window.clearTimeout(this.#mediaRevealTimeout);
    this.#mediaRevealTimeout = undefined;
    this.#mediaRevealReady = false;
    this.#mediaRevealAbortController?.abort();
    this.#mediaRevealImage = image;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const budget = getSpeculativeLoadingBudget();
    const isEager =
      image instanceof HTMLImageElement &&
      (image.loading === 'eager' || image.getAttribute('loading') === 'eager');
    const isReady = image instanceof HTMLImageElement && image.complete && image.naturalWidth > 0;
    const collection = this.closest('collection-component > results-list[data-catalog-view-contract="standard-lookbook"]');
    // loading is mutable after lookahead promotion. Keep only the explicitly
    // high-priority LCP image immediate; eager row companions can coalesce.
    const isCritical = isEager && (!collection || image.getAttribute('fetchpriority') === 'high');

    if (!(image instanceof HTMLImageElement) || reducedMotion || budget === 'none' || isCritical || (collection && isReady)) {
      gallery.removeAttribute('data-media-pending');
      return;
    }

    if (isReady) {
      const rect = this.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) return;
    }

    this.#mediaRevealReady = false;
    gallery.setAttribute('data-media-pending', '');
    getCardRevealObserver()?.observe(this);
    if (!isReady) promoteLazyImageForCurrentCatalogView(image);

    const settled = isReady || image.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
          this.#mediaRevealAbortController = new AbortController();
          const signal = this.#mediaRevealAbortController.signal;
          image.addEventListener('load', resolve, { once: true, signal });
          image.addEventListener('error', resolve, { once: true, signal });
        });

    void settled
      .then(() => (typeof image.decode === 'function' ? image.decode().catch(() => {}) : undefined))
      .then(() => {
        if (!this.isConnected || generation !== this.#mediaRevealGeneration || this.refs.cardGallery !== gallery) return;
        const currentImage = this.#mediaSlides()[0]?.querySelector('img') || gallery.querySelector('.product-media-container img');
        if (currentImage !== image) return;
        this.#mediaRevealAbortController?.abort();
        this.#mediaRevealAbortController = undefined;
        this.#mediaRevealReady = true;
        window.clearTimeout(this.#mediaRevealTimeout);
        this.#mediaRevealTimeout = undefined;
        if (!getCardRevealObserver()) this.revealReadyMedia();
        else queueReadyMediaReveal(this);
      });

    window.clearTimeout(this.#mediaRevealTimeout);
    this.#mediaRevealTimeout = window.setTimeout(() => {
      if (!this.isConnected || generation !== this.#mediaRevealGeneration) return;
      this.#mediaRevealReady = true;
      this.revealReadyMedia();
    }, CARD_MEDIA_REVEAL_FAILSAFE_MS);
  }

  isMediaRevealReady() {
    return this.#mediaRevealReady;
  }

  revealReadyMedia() {
    if (!this.#mediaRevealReady) return false;
    cancelReadyMediaReveal(this);
    this.#mediaRevealReady = false;
    window.clearTimeout(this.#mediaRevealTimeout);
    this.#mediaRevealTimeout = undefined;
    cardRevealObserver?.unobserve(this);
    this.refs.cardGallery?.removeAttribute('data-media-pending');
    return true;
  }

  warmLikelyMedia() {
    this.#revealPrimaryMedia();
  }

  warmNextMedia(intent = false) {
    if (intent) {
      adjacentMediaWarmQueue.delete(this);
      cardAdjacentMediaObserver?.unobserve(this);
    }
    if (!intent && getSpeculativeLoadingBudget() !== 'balanced') return Promise.resolve(false);
    const slides = this.#mediaSlides();
    const nextIndex = this.#currentMediaIndex() + 1;
    const image = slides[nextIndex]?.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return Promise.resolve(false);
    if (!intent && typeof image.decode !== 'function') return Promise.resolve(false);
    if (this.#adjacentMediaWarmImage === image && this.#adjacentMediaWarmPromise &&
        (!intent || !this.#adjacentMediaWaitingForPrimary)) {
      if (intent && image.fetchPriority !== 'high') image.fetchPriority = 'auto';
      return this.#adjacentMediaWarmPromise;
    }

    this.#adjacentMediaWarmController?.abort();
    const controller = new AbortController();
    this.#adjacentMediaWarmController = controller;
    this.#adjacentMediaWarmImage = image;
    const primary = slides[0]?.querySelector('img');
    this.#adjacentMediaWaitingForPrimary = !intent && primary instanceof HTMLImageElement && !primary.complete;
    const job = (async () => {
      if (this.#adjacentMediaWaitingForPrimary) {
        await new Promise((resolve) => {
          const settle = () => {
            window.clearTimeout(timer);
            primary.removeEventListener('load', settle);
            primary.removeEventListener('error', settle);
            controller.signal.removeEventListener('abort', settle);
            resolve();
          };
          const timer = window.setTimeout(settle, CARD_MEDIA_REVEAL_FAILSAFE_MS);
          primary.addEventListener('load', settle, { once: true });
          primary.addEventListener('error', settle, { once: true });
          controller.signal.addEventListener('abort', settle, { once: true });
        });
      }
      if (controller.signal.aborted || !this.isConnected || this.#mediaSlides()[nextIndex]?.querySelector('img') !== image) return false;
      if (!intent) {
        const bounds = this.getBoundingClientRect();
        if (getSpeculativeLoadingBudget() !== 'balanced' || bounds.bottom <= 0 || bounds.top > window.innerHeight + 200) return false;
        if (primary instanceof HTMLImageElement && primary.naturalWidth === 0) return false;
      }
      this.#adjacentMediaWaitingForPrimary = false;
      const priority = image.fetchPriority;
      image.fetchPriority = priority === 'high' ? 'high' : intent ? 'auto' : 'low';
      promoteLazyImageForCurrentCatalogView(image);
      image.fetchPriority = priority === 'high' ? 'high' : intent ? 'auto' : 'low';
      await image.decode?.();
      return !controller.signal.aborted && image.naturalWidth > 0;
    })().catch(() => false).then((ready) => {
      if (!ready && this.#adjacentMediaWarmPromise === job) {
        this.#adjacentMediaWarmImage = undefined;
        this.#adjacentMediaWarmPromise = undefined;
      }
      return ready;
    }).finally(() => {
      if (this.#adjacentMediaWarmController === controller) {
        this.#adjacentMediaWarmController = undefined;
        this.#adjacentMediaWaitingForPrimary = false;
      }
    });
    this.#adjacentMediaWarmPromise = job;
    return job;
  }

  #resetAdjacentMediaWarm() {
    adjacentMediaWarmQueue.delete(this);
    cardAdjacentMediaObserver?.unobserve(this);
    this.#adjacentMediaWarmController?.abort();
    this.#adjacentMediaWarmController = undefined;
    this.#adjacentMediaWarmImage = undefined;
    this.#adjacentMediaWarmPromise = undefined;
    this.#adjacentMediaWaitingForPrimary = false;
  }

  /**
   * Loads card media 3-5 only after real shopper intent.
   * @param {Document} [sourceDocument]
   * @returns {Promise<boolean>}
   */
  async hydrateDeferredMedia(sourceDocument) {
    const rail = this.#nativeRail();
    if (!rail) return false;

    if (!(sourceDocument instanceof Document)) this.#materializeLocalDeferredMedia();

    if (sourceDocument instanceof Document) {
      this.#deferredHydrationAbortController?.abort();
      this.#deferredHydrationAbortController = undefined;
      this.#deferredHydrationPromise = undefined;
      this.#deferredHydrationGeneration += 1;
      return this.#appendDeferredMedia(sourceDocument);
    }

    if (this.#deferredHydrated || !rail.hasAttribute('data-deferred-media')) {
      this.#deferredHydrated = true;
      return true;
    }

    if (this.#deferredHydrationPromise) return this.#deferredHydrationPromise;

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement) || !link.href) return false;

    const currentUrl = new URL(link.href, window.location.origin);
    const requestUrl = new URL(currentUrl.pathname, window.location.origin);
    const variantId = currentUrl.searchParams.get('variant');
    if (variantId) requestUrl.searchParams.set('variant', variantId);
    requestUrl.searchParams.set('section_id', 'section-rendering-product-card');

    const controller = new AbortController();
    const generation = ++this.#deferredHydrationGeneration;
    this.#deferredHydrationAbortController?.abort();
    this.#deferredHydrationAbortController = controller;

    const request = fetch(requestUrl.toString(), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product-card media render failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (controller.signal.aborted || generation !== this.#deferredHydrationGeneration || !this.isConnected) {
          return false;
        }
        return this.#appendDeferredMedia(new DOMParser().parseFromString(html, 'text/html'));
      })
      .catch((error) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.warn('[product-card] Deferred media failed:', error);
        }
        return false;
      })
      .finally(() => {
        if (this.#deferredHydrationPromise === request) this.#deferredHydrationPromise = undefined;
        if (this.#deferredHydrationAbortController === controller) this.#deferredHydrationAbortController = undefined;
      });

    this.#deferredHydrationPromise = request;
    return request;
  }

  /**
   * Selects a media item after a product-card swatch changes.
   * @param {string} mediaId
   * @param {Document} [sourceDocument]
   */
  async selectMediaById(mediaId, sourceDocument) {
    if (!mediaId) return;

    this.activateEnhancements();

    if (sourceDocument instanceof Document) await this.hydrateDeferredMedia(sourceDocument);

    let selected = this.#allMediaSlides().find((slide) => slide.getAttribute('slide-id') === String(mediaId));
    if (!selected) {
      await this.hydrateDeferredMedia();
      selected = this.#allMediaSlides().find((slide) => slide.getAttribute('slide-id') === String(mediaId));
    }
    if (!selected) return;

    for (const slide of this.#allMediaSlides()) {
      if (!slide.hasAttribute('variant-image')) continue;
      slide.toggleAttribute('hidden', slide.getAttribute('slide-id') !== String(mediaId));
    }

    this.#activateMediaRail();
    const index = this.#mediaSlides().indexOf(selected);
    if (index >= 0) {
      this.#scrollToMediaIndex(index, { animate: false });
      if (this.#collectionView === 'lookbook') {
        this.#standardMediaSlideId = selected.getAttribute('slide-id') || undefined;
      }
    }
  }

  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    }

    window.location.href = url.href;
  };

  activateEnhancements() {
    if (this.#enhancementsActive) {
      this.#flushCollectionMediaScroll();
      return;
    }
    this.#enhancementsActive = true;
    cardEnhancementObserver?.unobserve(this);

    this.addEventListener('click', this.#handleMediaRailClick);
    this.addEventListener('click', this.#handleCardVideoToggle);

    // The generic slideshow event router no longer owns product-card controls
    // once this card's richer media behavior is actually needed.
    for (const control of this.querySelectorAll(
      'slideshow-component[data-product-card-native] .slideshow-control[on\\:click]'
    )) {
      control.removeAttribute('on:click');
    }

    this.#syncMediaRailState(0);
    this.#setupCardVideoLifecycle();

    const resultsList = this.closest("results-list[data-catalog-view-contract='standard-lookbook']");
    const collectionGrid = resultsList?.querySelector('[product-grid-view]');
    if (collectionGrid instanceof HTMLElement) {
      this.syncCollectionView(collectionGrid.getAttribute('product-grid-view'));
    }
    this.#flushCollectionMediaScroll();

    if (this.refs.cardGallery instanceof HTMLElement) {
      getCardLookaheadObserver().observe(this);
      if (this.#pointerIntentListenersDeferred) getCardAdjacentMediaObserver()?.observe(this);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    // Keep product-view tracking (owned by ProductComponent) and navigation immediate.
    // Media/video/lookbook enhancement is activated only near the viewport or on intent.
    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('pointerdown', this.#handleMediaRailIntent, { passive: true });
    this.#pointerIntentListenersDeferred = Boolean(
      this.closest('collection-component > results-list[data-catalog-view-contract="standard-lookbook"]')
    );
    if (!this.#pointerIntentListenersDeferred) {
      this.addEventListener('pointermove', this.#handleMediaRailPointerMove, { passive: true });
      this.addEventListener('pointerup', this.#clearMediaRailPointerIntent, { passive: true });
      this.addEventListener('pointercancel', this.#clearMediaRailPointerIntent, { passive: true });
    }
    this.addEventListener('focusin', this.#handleMediaRailIntent);

    const observer = getCardEnhancementObserver();
    if (observer) observer.observe(this);
    else this.activateEnhancements();
  }

  updatedCallback() {
    this.#pendingCollectionMediaIndex = undefined;
    this.#resetAdjacentMediaWarm();
    this.#mediaRevealGeneration += 1;
    this.#mediaRevealAbortController?.abort();
    this.#mediaRevealAbortController = undefined;
    this.#mediaRevealImage = undefined;
    super.updatedCallback();

    cancelReadyMediaReveal(this);
    cardRevealObserver?.unobserve(this);
    window.clearTimeout(this.#mediaRevealTimeout);
    this.#mediaRevealTimeout = undefined;
    this.#mediaRevealReady = false;
    this.refs.cardGallery?.removeAttribute('data-media-pending');

    const rail = this.#nativeRail();
    if (!rail) return;

    this.#deferredHydrationAbortController?.abort();
    this.#deferredHydrationAbortController = undefined;
    this.#deferredHydrationGeneration += 1;
    this.#deferredHydrationPromise = undefined;
    this.#deferredHydrated = !rail.hasAttribute('data-deferred-media');

    this.#clearMediaRailPointerIntent();
    this.#hideMediaProgress();
    this.#galleryIntentConfirmed = false;
    this.#activeMediaIndex = 0;
    this.#standardMediaSlideId = undefined;
    this.#lookbookPreferredSlide = undefined;
    this.#lookbookPreferredPreviousSlide = undefined;
    this.#lookbookPreferredNextSlide = undefined;
    this.#pendingManualVideoPlay = false;
    this.#videoManuallyPaused = false;
    this.#standardVideoManuallyPaused = false;

    this.#videoObserver?.disconnect();
    this.#videoObserver = undefined;
    this.#videoAbortController?.abort();
    this.#videoAbortController = undefined;
    this.#pauseCardVideo();

    this.#deactivateMediaRail();
    if (this.#enhancementsActive) {
      this.#activateMediaRail();
      this.#syncMediaRailState(0);
      this.#setupCardVideoLifecycle();
    } else {
      this.#syncCollectionMediaScroll(0);
    }

    const grid = this.closest("results-list[data-catalog-view-contract='standard-lookbook']")?.querySelector(
      '[product-grid-view]'
    );
    const view = grid instanceof HTMLElement ? grid.getAttribute('product-grid-view') : null;
    this.#collectionView = 'standard';
    if (view === 'lookbook' || view === 'single') this.syncCollectionView(view);
    else this.#syncCollectionImageSizes('standard');
    if (this.#enhancementsActive && this.#pointerIntentListenersDeferred) getCardAdjacentMediaObserver()?.observe(this);
  }

  disconnectedCallback() {
    this.#pendingCollectionMediaIndex = undefined;
    this.#resetAdjacentMediaWarm();
    this.#mediaRevealGeneration += 1;
    this.#mediaRevealAbortController?.abort();
    this.#mediaRevealAbortController = undefined;
    this.#mediaRevealImage = undefined;
    cancelReadyMediaReveal(this);
    this.refs.cardGallery?.removeAttribute('data-media-pending');
    cardEnhancementObserver?.unobserve(this);
    cardLookaheadObserver?.unobserve(this);
    cardRevealObserver?.unobserve(this);
    window.clearTimeout(this.#mediaRevealTimeout);
    this.#mediaRevealTimeout = undefined;
    this.#mediaRevealReady = false;
    this.#videoObserver?.disconnect();
    this.#videoAbortController?.abort();
    this.#deferredHydrationAbortController?.abort();
    this.#deferredHydrationAbortController = undefined;
    this.#deferredHydrationGeneration += 1;
    this.#deferredHydrationPromise = undefined;
    this.#pauseCardVideo();
    this.#deactivateMediaRail();
    this.removeEventListener('click', this.#handleMediaRailClick);
    this.removeEventListener('click', this.#handleCardVideoToggle);
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerdown', this.#handleMediaRailIntent);
    this.removeEventListener('pointermove', this.#handleMediaRailPointerMove);
    this.removeEventListener('pointerup', this.#clearMediaRailPointerIntent);
    this.removeEventListener('pointercancel', this.#clearMediaRailPointerIntent);
    this.removeEventListener('focusin', this.#handleMediaRailIntent);
    this.#clearMediaRailPointerIntent();
    this.#enhancementsActive = false;
    this.#galleryIntentConfirmed = false;
    super.disconnectedCallback();
  }

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.hasAttribute('data-no-navigation')) return;
    if (event.target.closest('[data-tfp-brand-link]')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
    if (interactiveElement) return;

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const paginatedResultsList = this.closest(
      'results-list[infinite-scroll="true"], results-list[data-pagination-mode="adaptive"]'
    );
    if (!window.Shopify.designMode && paginatedResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) url.searchParams.set('page', parent.dataset.page);

      const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
      const isCollectionCatalog = paginatedResultsList.dataset.catalogViewContract === 'standard-lookbook';
      history.replaceState(isCollectionCatalog ? { ...state, tfpCollectionHistory: true } : {}, '', url.toString());
    }

    const targetLink = event.target.closest('a');
    if (!targetLink) this.#navigateToURL(event, linkURL);
  };
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
